import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Prisma } from '@prisma/client';
import { logout } from './auth';
import offersService from '../../api/services/offers';

export type offersWithUser = Prisma.offersGetPayload<{
  include: { user: true };
}>;

export type offersWithUserAndListing = Prisma.offersGetPayload<{
  include: { user: true; listings: true; currentRound: true };
}>;

interface State {
  offers: offersWithUserAndListing[];
  loading: boolean;
  /** True once a fetch has succeeded; lets callers tell "empty" from "not asked yet". */
  loaded: boolean;
  /** The fetch whose result may still land; cleared on logout so an old session's reply is dropped. */
  activeRequestId: string | null;
  error: string | null;
}

const initialState: State = {
  offers: [],
  loading: false,
  loaded: false,
  activeRequestId: null,
  error: null,
};

export const fetchOffers = createAsyncThunk(
  'offers/fetch',
  async (_params, { rejectWithValue }) => {
    try {
      const clients = await offersService.find();
      return clients;
    } catch (error) {
      return rejectWithValue('Failed to fetch offers');
    }
  }
);

const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // A different account signing in next must not inherit this one's data or loaded flag.
      .addCase(logout, () => initialState)
      .addCase(fetchOffers.pending, (state, action) => {
        state.activeRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchOffers.fulfilled,
        (state, action: PayloadAction<offersWithUserAndListing[], string, { requestId: string }>) => {
          if (action.meta.requestId !== state.activeRequestId) return;
          state.activeRequestId = null;
          state.loading = false;
          state.loaded = true;
          state.offers = action.payload;
        }
      )
      .addCase(fetchOffers.rejected, (state, action) => {
        if (action.meta.requestId !== state.activeRequestId) return;
        state.activeRequestId = null;
        state.loading = false;
        state.error = action.payload as string | null;
      });
  },
});

export default offersSlice.reducer;
